// Creator Learning — single module reader view.
// Route: /creator/learning/:module_id
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient, useAuth } from "../auth/AuthContext";
import { BrandFooter } from "../components/brand/Footer";
import { LogoLockup } from "../components/brand/LogoLockup";

function CreatorNav({ onLogout }) {
  return (
    <header className="border-b border-brand-text/10 bg-brand-bg/80 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-6">
          <LogoLockup variant="header" as={Link} to="/creator" />
          <nav className="hidden gap-6 text-[10px] uppercase tracking-[0.28em] text-brand-text/50 md:flex">
            <Link data-testid="nav-dashboard" to="/creator" className="hover:text-brand-gold">Dashboard</Link>
            <Link data-testid="nav-learning" to="/creator/learning" className="text-brand-gold">Learning</Link>
            <Link data-testid="nav-profile"   to="/profile" className="hover:text-brand-gold">My Profile</Link>
            <Link data-testid="nav-contract"  to="/contract" className="hover:text-brand-gold">My Contract</Link>
          </nav>
        </div>
        <button
          data-testid="nav-signout"
          onClick={onLogout}
          className="rounded-full border border-brand-text/15 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-brand-text/60 hover:border-brand-gold/40 hover:text-brand-gold"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

export default function CreatorLearningModule() {
  const { module_id } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    apiClient.get(`/learning/modules/${module_id}`)
      .then((r) => {
        setMod(r.data);
        document.title = `${r.data.title} · Learning`;
      })
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "fetch_failed"));
  }, [module_id]);

  const markRead = async () => {
    setMarking(true);
    try {
      const r = await apiClient.post(`/me/learning/modules/${module_id}/read`);
      setMod((m) => ({ ...m, read_at: r.data.read_at }));
    } finally { setMarking(false); }
  };
  const onSignOut = async () => { await logout(); navigate("/", { replace: true }); };

  if (error === "not_found") {
    return (
      <main className="min-h-screen bg-brand-bg text-brand-text">
        <CreatorNav onLogout={onSignOut} />
        <div data-testid="module-not-found" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Module not available.</h1>
          <p className="mt-3 text-sm text-brand-text/60">It may have been unpublished. <Link to="/creator/learning" className="text-brand-gold">Back to library</Link>.</p>
        </div>
      </main>
    );
  }
  if (!mod) return (
    <main className="min-h-screen bg-brand-bg text-brand-text">
      <CreatorNav onLogout={onSignOut} />
      <div className="mx-auto max-w-3xl px-6 py-24 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45">Loading…</div>
    </main>
  );

  return (
    <main data-testid="module-reader" className="min-h-screen bg-brand-bg text-brand-text">
      <CreatorNav onLogout={onSignOut} />

      <article className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
        <Link to="/creator/learning" data-testid="reader-back-link" className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/50 hover:text-brand-gold">← Back to library</Link>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span data-testid="reader-category" className="rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-gold">{mod.category_label}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/50">~{mod.estimated_read_minutes} min read</span>
          {mod.read_at && (
            <span data-testid="reader-read-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-300">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              Read {new Date(mod.read_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
        <h1 data-testid="reader-title" className="mt-4 font-display text-4xl text-brand-text sm:text-5xl">{mod.title}</h1>

        {mod.hero_image_url && (
          <img src={mod.hero_image_url} alt="" className="mt-8 w-full rounded-2xl border border-brand-text/10" />
        )}

        {/* Body — sanitised on the server (bleach). Safe to inject. */}
        <div
          data-testid="reader-body"
          className="prose prose-invert mt-8 max-w-prose text-brand-text/80
                     [&_p]:my-4 [&_p]:text-base [&_p]:leading-relaxed
                     [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-brand-text
                     [&_h3]:mt-8 [&_h3]:mb-2 [&_h3]:font-display [&_h3]:text-xl [&_h3]:text-brand-text
                     [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-5
                     [&_li]:my-1 [&_strong]:text-brand-text [&_em]:text-brand-gold
                     [&_a]:text-brand-gold [&_a]:underline hover:[&_a]:text-brand-gold/80
                     [&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-brand-gold/40 [&_blockquote]:pl-4 [&_blockquote]:italic"
          dangerouslySetInnerHTML={{ __html: mod.body_html }}
        />

        {mod.video_url && (
          <a
            data-testid="reader-video-link"
            href={mod.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-brand-gold/60 px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
          >
            Watch companion video ↗
          </a>
        )}

        <div className="mt-12 border-t border-brand-text/10 pt-6">
          {!mod.read_at ? (
            <button
              data-testid="reader-mark-read"
              onClick={markRead}
              disabled={marking}
              className="rounded-full border border-brand-gold bg-brand-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.28em] text-brand-bg transition hover:bg-brand-bg hover:text-brand-gold disabled:opacity-50"
            >
              {marking ? "Marking…" : "Mark as read"}
            </button>
          ) : (
            <span data-testid="reader-already-read" className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-300">
              ✓ You marked this read on {new Date(mod.read_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </article>
      <BrandFooter />
    </main>
  );
}
